/**
 * EtsyReturnPolicySelector — bloc Settings → Atelier qui permet à
 * Stéphanie de choisir la return policy Etsy par défaut.
 *
 * Contexte : Etsy v3 a rendu `return_policy_id` obligatoire sur les
 * listings physical/both en 2026-05 (HTTP 400 "/return/policy: cannot
 * be null"). Stéphanie choisit dans la liste de ses return policies
 * Etsy ; le choix est persisté dans `app_settings.etsy_default_return_policy_id`.
 *
 * Comportement miroir `EtsyReadinessStateSelector` /
 * `EtsyShippingProfileSelector`.
 */

import { ExternalLink, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import {
  useEtsyDefaultReturnPolicy,
  useEtsyReturnPolicies,
  useUpdateEtsyDefaultReturnPolicy,
} from '@/hooks/useAtelierSettings'
import { usePlatformConnections } from '@/hooks/usePlatformConnections'

export default function EtsyReturnPolicySelector() {
  const connectionsQuery = usePlatformConnections()
  const settingQuery = useEtsyDefaultReturnPolicy()
  const updateMutation = useUpdateEtsyDefaultReturnPolicy()

  const etsyConnection = useMemo(() => {
    const conns = connectionsQuery.data ?? []
    return conns.find((c) => c.platform === 'etsy' && !c.revoked_at) ?? null
  }, [connectionsQuery.data])

  const policiesQuery = useEtsyReturnPolicies(etsyConnection?.id ?? null)

  const [selectedId, setSelectedId] = useState<number | null>(settingQuery.data?.value ?? null)
  const [manualInput, setManualInput] = useState<string>(
    settingQuery.data?.value !== null && settingQuery.data?.value !== undefined
      ? String(settingQuery.data.value)
      : '',
  )
  const [prevServerValue, setPrevServerValue] = useState<number | null | undefined>(
    settingQuery.data?.value,
  )
  if (settingQuery.data && settingQuery.data.value !== prevServerValue) {
    setPrevServerValue(settingQuery.data.value)
    setSelectedId(settingQuery.data.value)
    setManualInput(settingQuery.data.value !== null ? String(settingQuery.data.value) : '')
  }

  const isLoading = connectionsQuery.isLoading || settingQuery.isLoading
  const policiesUnavailable = etsyConnection !== null && policiesQuery.isError
  const showFallbackInput = etsyConnection === null || policiesUnavailable

  const handleSave = () => {
    const valueToPost = showFallbackInput
      ? manualInput.trim() === ''
        ? null
        : Number.parseInt(manualInput, 10)
      : selectedId
    if (valueToPost !== null && (!Number.isFinite(valueToPost) || valueToPost < 1)) {
      toast.error('ID return policy invalide — saisis un entier ≥ 1')
      return
    }
    updateMutation.mutate(
      { value: valueToPost },
      {
        onSuccess: () => {
          toast.success(
            valueToPost === null
              ? 'Return policy par défaut effacée'
              : `Return policy par défaut mise à jour (#${valueToPost})`,
          )
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Sauvegarde impossible')
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div>
        <header className="settings-section-header">
          <div>
            <h2 className="ui-h4 settings-section-title">Politique de retour (Etsy)</h2>
          </div>
        </header>
        <p className="atelier-shipping-loading">
          <Loader2 className="atelier-shipping-spinner" size={16} />
          Chargement…
        </p>
      </div>
    )
  }

  const formatPolicyLabel = (p: {
    return_policy_id: number
    accepts_returns: boolean
    accepts_exchanges: boolean
    return_deadline: number | null
  }) => {
    const parts: string[] = []
    if (p.accepts_returns) parts.push('Retours')
    if (p.accepts_exchanges) parts.push('Échanges')
    if (parts.length === 0) parts.push('Pas de retour')
    if (p.return_deadline) parts.push(`${p.return_deadline}j`)
    return `${parts.join(' • ')} (#${p.return_policy_id})`
  }

  return (
    <div>
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Politique de retour (Etsy)</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Etsy exige maintenant une <strong>return policy</strong> sur chaque listing physique.
            Sélectionne celle que tu utilises par défaut.
          </p>
        </div>
      </header>

      {etsyConnection === null && (
        <div className="ui-alert ui-alert--info" role="status">
          Etsy n'est pas connecté. Connecte ton compte Etsy depuis l'onglet{' '}
          <strong>Diffusion</strong> pour voir la liste — ou saisis manuellement l'ID ci-dessous.
        </div>
      )}

      {policiesUnavailable && (
        <div className="ui-alert ui-alert--info" role="status">
          Impossible de lire les return policies depuis Etsy (token expiré ou API indisponible).
          Reconnecte Etsy, ou saisis l'ID manuellement ci-dessous.
        </div>
      )}

      <div className="atelier-shipping-fieldset">
        {!showFallbackInput && policiesQuery.data && policiesQuery.data.length === 0 && (
          <div className="ui-alert ui-alert--warning" role="status">
            <p className="ui-body">
              <strong>Aucune return policy trouvée côté Etsy.</strong> Crée-en une d'abord depuis
              ton seller dashboard puis recharge.
            </p>
            <p className="atelier-shipping-link">
              <a
                href="https://www.etsy.com/your/shops/me/policy-settings"
                target="_blank"
                rel="noreferrer noopener"
              >
                Ouvrir Etsy → Policy settings
                <ExternalLink size={12} />
              </a>
            </p>
          </div>
        )}

        {!showFallbackInput && policiesQuery.data && policiesQuery.data.length > 0 && (
          <Field
            label="Return policy par défaut"
            helper="Sélectionne la politique de retour que tu utilises pour tes tirages."
          >
            {(controlProps) => (
              <Select
                {...controlProps}
                value={selectedId === null ? '' : String(selectedId)}
                onChange={(e) =>
                  setSelectedId(e.target.value === '' ? null : Number.parseInt(e.target.value, 10))
                }
              >
                <option value="">— Aucune policy —</option>
                {policiesQuery.data.map((p) => (
                  <option key={p.return_policy_id} value={p.return_policy_id}>
                    {formatPolicyLabel(p)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {showFallbackInput && (
          <>
            <Field
              label="ID de la return policy Etsy"
              helper="Identifiant numérique de la return policy (visible dans l'URL côté Etsy seller)."
            >
              {(controlProps) => (
                <Input
                  {...controlProps}
                  type="number"
                  min={1}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="ex: 12345"
                />
              )}
            </Field>
            <p className="atelier-shipping-link">
              <a
                href="https://www.etsy.com/your/shops/me/policy-settings"
                target="_blank"
                rel="noreferrer noopener"
              >
                Ouvrir mes return policies Etsy
                <ExternalLink size={12} />
              </a>
            </p>
          </>
        )}

        <div className="atelier-shipping-actions">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Sauvegarde…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  )
}
